import React, { FC, useContext, useState, useEffect, useMemo } from "react";
import { defineMessages, IntlContext } from "react-intl";
import Svg from "@natera/material/lib/svg";
import {
  BillingPaymentPlanStatus,
  StatementStatus,
} from "@app/postBill/models";
import errorIcon from "../../assets/svg/error.svg";
import pendingIcon from "../../assets/svg/pending.svg";
import checkIcon from "../../assets/svg/check.svg";
import activeIcon from "../../assets/svg/active.svg";
import "./statusChip.scss";

const messages = defineMessages({
  billingStatusPaid: {
    id: "billingStatusPaid",
    defaultMessage: "Paid",
  },
  billingStatusPastDue: {
    id: "billingStatusPastDue",
    defaultMessage: "Past due",
  },
  billingStatusFinalNotice: {
    id: "billingStatusFinalNotice",
    defaultMessage: "Final notice",
  },
  billingStatusPreCollection: {
    id: "billingStatusPreCollection",
    defaultMessage: "Pre-collection",
  },
  billingStatusNateraCollections: {
    id: "billingStatusNateraCollections",
    defaultMessage: "Natera Collections",
  },
  billingStatusPaymentPending: {
    id: "billingStatusPaymentPending",
    defaultMessage: "Payment pending",
  },
  billingStatusClaimPending: {
    id: "billingStatusClaimPending",
    defaultMessage: "Claim pending",
  },
  billingStatusActive: {
    id: "billingStatusActive",
    defaultMessage: "Active",
  },
  billingStatusCanceled: {
    id: "billingStatusCanceled",
    defaultMessage: "Canceled",
  },
});

interface StatusChipProps {
  status: string;
}

type StatusChipType = "active" | "success" | "error" | "pending";

type AllStatuses = StatementStatus | BillingPaymentPlanStatus;

type StatusDescription = {
  [key: string]: string;
};

const StatusChip: FC<StatusChipProps> = ({ status }) => {
  const intl = useContext(IntlContext);
  const statusDescription: StatusDescription = {
    [StatementStatus.PAID]: intl.formatMessage(messages.billingStatusPaid),
    [StatementStatus.PAST_DUE]: intl.formatMessage(
      messages.billingStatusPastDue
    ),
    [StatementStatus.FINAL_NOTICE]: intl.formatMessage(
      messages.billingStatusFinalNotice
    ),
    [StatementStatus.PRE_COLLECTION]: intl.formatMessage(
      messages.billingStatusPreCollection
    ),
    [StatementStatus.NATERA_COLLECTIONS]: intl.formatMessage(
      messages.billingStatusNateraCollections
    ),
    [StatementStatus.PAYMENT_PENDING]: intl.formatMessage(
      messages.billingStatusPaymentPending
    ),
    [StatementStatus.CLAIM_PENDING]: intl.formatMessage(
      messages.billingStatusClaimPending
    ),
    [BillingPaymentPlanStatus.ACTIVE]: intl.formatMessage(
      messages.billingStatusActive
    ),
    [BillingPaymentPlanStatus.CANCELLED]: intl.formatMessage(
      messages.billingStatusCanceled
    ),
    [BillingPaymentPlanStatus.COMPLETED]: intl.formatMessage(
      messages.billingStatusPaid
    ),
  };

  const STATUS_MAP: Record<StatusChipType, AllStatuses[]> = {
    pending: [StatementStatus.PAYMENT_PENDING, StatementStatus.CLAIM_PENDING],
    success: [StatementStatus.PAID, BillingPaymentPlanStatus.COMPLETED],
    active: [BillingPaymentPlanStatus.ACTIVE],
    error: [
      StatementStatus.FINAL_NOTICE,
      StatementStatus.PAST_DUE,
      StatementStatus.PRE_COLLECTION,
      BillingPaymentPlanStatus.CANCELLED,
      StatementStatus.NATERA_COLLECTIONS,
    ],
  };

  const HIDDEN_STATUSES: AllStatuses[] = [
    //Billing
    StatementStatus.READY_TO_PAY,
    StatementStatus.PARTIAL_PENDING,
  ];

  const statusData = useMemo(() => {
    const isVisible = !HIDDEN_STATUSES.includes(status as AllStatuses);
    const foundEntry = (
      Object.entries(STATUS_MAP) as [StatusChipType, AllStatuses[]][]
    ).find(([_, values]) => values.includes(status as AllStatuses));

    const type: StatusChipType = foundEntry ? foundEntry[0] : "success";

    const config: Record<StatusChipType, { icon: string; className: string }> =
      {
        pending: { icon: pendingIcon, className: "status-pending" },
        success: { icon: checkIcon, className: "" },
        active: { icon: activeIcon, className: "status-active" },
        error: { icon: errorIcon, className: "status-error" },
      };

    return {
      isVisible,
      ...config[type],
    };
  }, [status]);

  return (
    <>
      {statusData.isVisible && (
        <div className={`status-chip ${statusData.className}`}>
          <div className="icon">
            <Svg content={statusData.icon} />
          </div>
          <div className="text">{statusDescription[status]}</div>
        </div>
      )}
    </>
  );
};

export default StatusChip;
